import React from "react";
import yup from "yup";
import { useObserver } from "mobx-react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  FormControl,
  FormLabel,
  IconButton
} from "@material-ui/core";
import { Add, Delete } from "@material-ui/icons";
import { useFormStore } from "@modules/formx-react/useFormStore";
import RegularButton from "@components/buttons/RegularButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import locale from "@bonliva-crm/locale";
import { FormxDebugger, Form, Time2, ToggleButtonGroup } from "@common/formx";
import { fastOptions, weekDays } from "./utils";

type Props = {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: ExtendedPeriodFormData) => void;
  initialValues?: ExtendedPeriodFormData;
  periodsTitle: string;
  showWeekDaysInput: boolean;
};

export type ExtendedPeriodFormData = {
  from: string;
  to: string;
  periods: Array<{ from: string; to: string }>;
};

const schema = yup.object<ExtendedPeriodFormData>({
  from: yup.string().required(locale.From),
  to: yup.string().required(locale.To),
  periods: yup.array().of(
    yup.object<{ from: string; to: string }>({
      from: yup.string().required(locale.From),
      to: yup.string().required(locale.To)
    })
  )
});

export function ExtendedPeriodForm(props: Props) {
  const formStore = useFormStore<ExtendedPeriodFormData>({
    schema,
    initialValues: props.initialValues || { from: "", to: "", periods: [] },
    onSubmit: async () => {
      props.onSubmit(formStore.values);
    }
  });

  const setMainValues = (from: string, to: string) => {
    formStore.setValue("to", to);
    formStore.setValue("from", from);
  };

  const removePeriod = (index: number) => {
    const periods = [...formStore.values.periods];
    periods.splice(index, 1);
    formStore.setValue("periods", periods);
  };

  return useObserver(() => {
    return (
      <Dialog
        open={props.open}
        disableBackdropClick
        onClose={props.onClose}
        onBackdropClick={props.onClose}
        onEntered={(node) => {
          node.style.overflow = "visible";
        }}
        onExit={(node) => (node.style.overflow = "hidden")}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {locale.EditTime}: {props.title}
        </DialogTitle>

        <DialogContent style={{ overflow: "auto" }}>
          <Form store={formStore} allowUpdates>
            <FormxDebugger store={formStore} />

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Time2
                  name="from"
                  DatePickerProps={{
                    timeIntervals: 15,
                    fullWidth: true,
                    // autoOpen: !props.initialValues,
                    clearable: false
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Time2
                  name="to"
                  DatePickerProps={{
                    timeIntervals: 15,
                    fullWidth: true,
                    clearable: false
                  }}
                />
              </Grid>

              {fastOptions.length > 0 && (
                <Grid item xs={12} style={{ paddingTop: 16 }}>
                  {fastOptions.map((opt, i) => (
                    <Button
                      key={i}
                      onClick={() => setMainValues(opt.from, opt.to)}
                      variant="outlined"
                      style={{ marginRight: 8 }}
                    >
                      {opt.from}-{opt.to}
                    </Button>
                  ))}
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle1">{props.periodsTitle}</Typography>

                <FormControl fullWidth style={{ padding: "10px 0" }} key={"TimeSpans"}>
                  <FormLabel component="legend" style={{ padding: "10px 0" }}>
                    <span style={{ marginRight: "15px" }}>{locale.Periods}</span>
                    <IconButton
                      aria-label="add"
                      onClick={() =>
                        formStore.push("periods", {
                          from: "",
                          to: ""
                        })
                      }
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </FormLabel>

                  <Grid item xs={12} container spacing={1} alignItems={"center"}>
                    {formStore.values.periods.map((x, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={5}>
                          <Time2
                            name={`periods.${index}.from`}
                            DatePickerProps={{
                              timeIntervals: 15,
                              fullWidth: true,
                              clearable: false
                            }}
                          />
                        </Grid>

                        <Grid item xs={5}>
                          <Time2
                            name={`periods.${index}.to`}
                            DatePickerProps={{
                              timeIntervals: 15,
                              fullWidth: true,
                              clearable: false
                            }}
                          />
                        </Grid>

                        <Grid item xs={2}>
                          <IconButton onClick={() => removePeriod(index)} size="small">
                            <Delete />
                          </IconButton>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                </FormControl>
              </Grid>
              {props.showWeekDaysInput && (
                <Grid item xs={12}>
                  <ToggleButtonGroup
                    name="weekDays"
                    exclusive={false}
                    wrap
                    label={locale.RepeatOnWeekDays}
                    options={weekDays.map((l, i) => ({
                      label: l,
                      value: i
                    }))}
                  />
                </Grid>
              )}
            </Grid>
          </Form>
        </DialogContent>

        <DialogActions>
          <RegularButton style={{ marginRight: "auto" }} onClick={props.onClose}>
            {locale.Cancel}
          </RegularButton>

          <PrimaryButton
            disabled={!formStore.canSubmit}
            onClick={() => formStore.submit()}
          >
            OK
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    );
  });
}
